import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { isVersionConflict, apiErrorMessage } from '@/lib/conflict';
import { useAuthStore } from '@/stores/auth';
import { UserCell } from '@/components/ui/user-cell';
import { RoleBadge } from '@/components/ui/role-badge';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingPage } from '@/components/ui/loading-spinner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Users, Mail, Send, Clock, Trash2, Shield, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatTeamRole } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface TeamMember {
  id: string;
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  status: 'active' | 'invited' | 'suspended';
  featureAccess: Record<string, boolean>;
  invitedAt: string;
  joinedAt?: string;
  /** Backend marks members true when their userId no longer resolves
   *  in users collection (deleted-user-with-leftover-membership). */
  orphaned?: boolean;
  /** Optimistic-concurrency counter from the server; absent (legacy doc) = 0. */
  version?: number;
}

interface TeamStats {
  memberCount: number;
  projectCount: number;
  pluginCount: number;
  orderCount: number;
  customerCount: number;
  blogPostCount: number;
  plan: string;
  hasActiveSubscription: boolean;
  isEmptyDummy: boolean;
  /** True when ANY active member is a SUPER_ADMIN. Such teams are
   *  treated as platform-essential and never get the safe-to-delete hint. */
  hasSuperAdminMember?: boolean;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
}

interface TeamSummary {
  id: string;
  name: string;
  plan?: string;
}

type ConfirmTarget = {
  type: 'remove-member' | 'revoke-invite';
  id: string;
  label: string;
};

// The team-picker list, per-team invitations, and per-team stats don't yet
// have slots in the qk factory, so key them locally.
const TEAMS_KEY = ['teams', 'all'] as const; // TODO: promote to qk
const teamInvitationsKey = (teamId: string) => ['team', teamId, 'invitations'] as const; // TODO: promote to qk
const teamStatsKey = (teamId: string) => ['team', teamId, 'stats'] as const; // TODO: promote to qk

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function TeamSettingsPage() {
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const queryTeamId = searchParams.get('teamId') || '';

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('editor');

  // Delete-team confirmation modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTypedName, setDeleteTypedName] = useState('');

  // Remove-member / revoke-invite confirmation state
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget | null>(null);

  // ── Reads ───────────────────────────────────────────────────────────────
  // The resolved team. Either SUPER_ADMIN switched teams (or a non-super
  // landed on a deep link) via ?teamId, or we fall back to /teams/me. The
  // backend still 403s if a non-super requests a team they aren't a member
  // of, so no client-side gating is needed.
  const teamQuery = useQuery({
    queryKey: qk.team(queryTeamId || 'me'),
    queryFn: async () => {
      const res = queryTeamId ? await api.get(`/teams/${queryTeamId}`) : await api.get('/teams/me');
      return res.data.data as { id: string; name?: string };
    },
  });
  const teamId = teamQuery.data?.id ?? '';
  const teamName = teamQuery.data?.name ?? '';

  // SUPER_ADMIN team switcher — load list of all teams for the picker.
  const allTeamsQuery = useQuery({
    queryKey: TEAMS_KEY,
    queryFn: async () => (await api.get('/teams')).data.data as TeamSummary[],
    enabled: isSuperAdmin,
  });
  const allTeams = allTeamsQuery.data ?? [];

  const membersQuery = useQuery({
    queryKey: qk.teamMembers(teamId),
    queryFn: async () => (await api.get(`/teams/${teamId}/members`)).data.data as TeamMember[],
    enabled: Boolean(teamId),
  });
  const members = membersQuery.data ?? [];

  const invitationsQuery = useQuery({
    queryKey: teamInvitationsKey(teamId),
    queryFn: async () => (await api.get(`/teams/${teamId}/invitations`)).data.data as Invitation[],
    enabled: Boolean(teamId),
  });
  const invitations = invitationsQuery.data ?? [];

  const statsQuery = useQuery({
    queryKey: teamStatsKey(teamId),
    queryFn: async () => {
      try {
        return (await api.get(`/teams/${teamId}/stats`)).data.data as TeamStats;
      } catch {
        return null;
      }
    },
    enabled: Boolean(teamId),
  });
  const stats = statsQuery.data ?? null;

  // Mirror the old single "Failed to load team" toast without spamming one
  // per failing read.
  useEffect(() => {
    if (teamQuery.isError || membersQuery.isError || invitationsQuery.isError) {
      toast.error('Failed to load team');
    }
  }, [teamQuery.isError, membersQuery.isError, invitationsQuery.isError]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const inviteMutation = useMutation({
    mutationFn: async () =>
      api.post(`/teams/${teamId}/invite`, { email: inviteEmail, role: inviteRole }),
    onSuccess: () => {
      toast.success('Invitation sent');
      setInviteEmail('');
      queryClient.invalidateQueries({ queryKey: teamInvitationsKey(teamId) });
      queryClient.invalidateQueries({ queryKey: teamStatsKey(teamId) });
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to send invitation'));
    },
  });

  const roleMutation = useMutation({
    // Optimistic concurrency: send the member version this list was rendered
    // from so the server rejects (409) if someone else changed the member in
    // the meantime — instead of silently clobbering their change.
    mutationFn: async ({
      userId,
      role,
      expectedVersion,
    }: {
      userId: string;
      role: string;
      expectedVersion: number;
    }) => api.patch(`/teams/${teamId}/members/${userId}`, { role, expectedVersion }),
    onSuccess: (_res, { role }) => {
      toast.success(`Role updated to ${formatTeamRole(role)}`);
      queryClient.invalidateQueries({ queryKey: qk.teamMembers(teamId) });
    },
    onError: (err) => {
      if (isVersionConflict(err)) {
        // A dropdown pick has no draft worth preserving — refresh the list so
        // the admin sees what changed, and let them re-pick if still needed.
        toast.error(
          'This member was changed by someone else — the list has been refreshed. Please retry.'
        );
        queryClient.invalidateQueries({ queryKey: qk.teamMembers(teamId) });
        return;
      }
      toast.error(apiErrorMessage(err, 'Failed to update role'));
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: async () =>
      (await api.delete(`/teams/${teamId}`, { data: { confirm: teamName } })).data.data,
    onSuccess: (result) => {
      toast.success(`Team "${teamName}" deleted.`);
      // Show any warnings about resources NOT cleaned up (Worker scripts, R2, subs).
      for (const w of result?.warnings ?? []) {
        toast.warning(w, { duration: 10_000 });
      }
      // Reset to "my team" view and refresh the team picker list.
      setDeleteOpen(false);
      setDeleteTypedName('');
      const next = new URLSearchParams(searchParams);
      next.delete('teamId');
      setSearchParams(next);
      if (isSuperAdmin) {
        queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
      } else {
        // Non-superadmin deleted their own team — bounce to dashboard.
        navigate('/dashboard');
      }
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to delete team'));
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async (target: ConfirmTarget) => {
      if (target.type === 'remove-member') {
        await api.delete(`/teams/${teamId}/members/${target.id}`);
      } else {
        await api.delete(`/teams/${teamId}/invitations/${target.id}`);
      }
      return target;
    },
    onSuccess: (target) => {
      if (target.type === 'remove-member') {
        toast.success('Member removed');
        queryClient.invalidateQueries({ queryKey: qk.teamMembers(teamId) });
        queryClient.invalidateQueries({ queryKey: teamStatsKey(teamId) });
      } else {
        toast.success('Invitation revoked');
        queryClient.invalidateQueries({ queryKey: teamInvitationsKey(teamId) });
      }
      setConfirmTarget(null);
    },
    onError: (_err, target) => {
      toast.error(
        target.type === 'remove-member' ? 'Failed to remove member' : 'Failed to revoke invitation'
      );
      setConfirmTarget(null);
    },
  });

  // ── Handlers ──────────────────────────────────────────────────────────────
  function handleDeleteTeam() {
    if (!teamId || !teamName) return;
    if (deleteTypedName !== teamName) {
      toast.error('Team name does not match — type it exactly to confirm.');
      return;
    }
    deleteTeamMutation.mutate();
  }

  function handleTeamSwitch(nextId: string) {
    const next = new URLSearchParams(searchParams);
    if (!nextId) {
      // Empty option = clear param → falls back to /teams/me on next load
      next.delete('teamId');
    } else {
      next.set('teamId', nextId);
    }
    setSearchParams(next);
  }

  function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail || !teamId) return;
    inviteMutation.mutate();
  }

  function handleRoleChange(member: TeamMember, newRole: string) {
    roleMutation.mutate({
      userId: member.userId,
      role: newRole,
      expectedVersion: member.version ?? 0,
    });
  }

  function handleConfirmAction() {
    if (!confirmTarget) return;
    confirmMutation.mutate(confirmTarget);
  }

  const deleting = deleteTeamMutation.isPending;

  // Skeleton only while the primary reads have no data yet — background
  // refetches keep the already-loaded team on screen.
  if (teamQuery.isPending || (Boolean(teamId) && membersQuery.isPending)) return <LoadingPage />;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">
            {isSuperAdmin && teamName ? `${teamName} — Team Settings` : 'Team Settings'}
          </h1>
          <p className="text-muted-foreground">
            {isSuperAdmin
              ? 'Manage any team’s members and invitations. Select a team above to switch.'
              : 'Manage your team members and invitations.'}
          </p>
          {stats && (
            <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
              <span className="bg-muted rounded px-2 py-0.5">
                Members: <strong className="text-foreground">{stats.memberCount}</strong>
              </span>
              <span className="bg-muted rounded px-2 py-0.5">
                Projects: <strong className="text-foreground">{stats.projectCount}</strong>
              </span>
              <span className="bg-muted rounded px-2 py-0.5">
                Plugins: <strong className="text-foreground">{stats.pluginCount}</strong>
              </span>
              {stats.orderCount > 0 && (
                <span className="bg-muted rounded px-2 py-0.5">
                  Orders: <strong className="text-foreground">{stats.orderCount}</strong>
                </span>
              )}
              {stats.customerCount > 0 && (
                <span className="bg-muted rounded px-2 py-0.5">
                  CRM contacts: <strong className="text-foreground">{stats.customerCount}</strong>
                </span>
              )}
              {stats.blogPostCount > 0 && (
                <span className="bg-muted rounded px-2 py-0.5">
                  Blog posts: <strong className="text-foreground">{stats.blogPostCount}</strong>
                </span>
              )}
              <span className="rounded bg-blue-100 px-2 py-0.5 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                Plan: <strong>{stats.plan}</strong>
              </span>
              {stats.hasActiveSubscription && (
                <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  ⚠ active subscription
                </span>
              )}
              {stats.isEmptyDummy && (
                <span className="rounded bg-green-100 px-2 py-0.5 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  ✓ safe to delete
                </span>
              )}
              {stats.hasSuperAdminMember && (
                <span
                  className="rounded bg-purple-100 px-2 py-0.5 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                  title="A SUPER_ADMIN is a member of this team — treat as platform-essential."
                >
                  🛡 SUPER_ADMIN member
                </span>
              )}
            </div>
          )}
        </div>

        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <Shield className="text-muted-foreground h-4 w-4" />
            <select
              value={queryTeamId}
              onChange={(e) => handleTeamSwitch(e.target.value)}
              className="bg-background focus:ring-primary min-w-[14rem] rounded-lg border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2"
              title="Switch team (SUPER_ADMIN)"
            >
              <option value="">My team ({teamName || '…'})</option>
              {allTeams
                .filter((t) => t.id !== teamId || queryTeamId)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.plan ? ` — ${t.plan}` : ''}
                  </option>
                ))}
            </select>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Invite Form                                                        */}
      {/* ------------------------------------------------------------------ */}
      <div className="bg-card rounded-xl border p-6">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
          <Send className="h-5 w-5" />
          Invite Member
        </h2>
        <form onSubmit={handleInvite} className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            placeholder="Email address"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="bg-background focus:ring-primary flex-1 rounded-lg border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2"
            required
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            className="bg-background focus:ring-primary rounded-lg border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2"
          >
            <option value="admin">Admin</option>
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
          <button
            type="submit"
            disabled={inviteMutation.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {inviteMutation.isPending ? (
              'Sending...'
            ) : (
              <>
                <Mail className="h-4 w-4" /> Invite
              </>
            )}
          </button>
        </form>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Members List                                                       */}
      {/* ------------------------------------------------------------------ */}
      <div className="bg-card rounded-xl border">
        <div className="border-b px-6 py-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Users className="h-5 w-5" />
            Members ({members.length})
          </h2>
        </div>

        {members.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No members yet"
            description="Invite someone to join your team."
            className="border-0"
          />
        ) : (
          <div className="divide-y">
            {members.map((member) => (
              <div key={member.id} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-4">
                  {member.orphaned ? (
                    <div className="flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                        <AlertTriangle className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-foreground text-sm font-medium">Deleted user</p>
                        <p className="text-muted-foreground text-xs">
                          orphaned membership · userId {member.userId.slice(-6)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <UserCell
                      id={member.userId}
                      firstName={member.firstName ?? undefined}
                      lastName={member.lastName ?? undefined}
                      email={member.email ?? undefined}
                    />
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {member.role === 'owner' ? (
                    <RoleBadge role={member.role} />
                  ) : (
                    <select
                      value={member.role}
                      onChange={(e) => handleRoleChange(member, e.target.value)}
                      className="bg-background focus:ring-primary rounded-lg border px-2 py-1 text-xs font-medium focus:border-transparent focus:outline-none focus:ring-2"
                    >
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  )}
                  {member.role !== 'owner' && (
                    <button
                      onClick={() =>
                        setConfirmTarget({
                          type: 'remove-member',
                          id: member.userId,
                          label: member.firstName
                            ? `${member.firstName} ${member.lastName ?? ''}`
                            : (member.email ?? 'this member'),
                        })
                      }
                      className="text-muted-foreground rounded p-1 transition-colors hover:text-red-600"
                      title="Remove member"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Pending Invitations                                                */}
      {/* ------------------------------------------------------------------ */}
      {invitations.length > 0 && (
        <div className="bg-card rounded-xl border">
          <div className="border-b px-6 py-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Clock className="h-5 w-5" />
              Pending Invitations ({invitations.length})
            </h2>
          </div>
          <div className="divide-y">
            {invitations.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-4">
                  <UserCell id={invite.id} email={invite.email} size="sm" />
                </div>

                <div className="flex items-center gap-3">
                  <RoleBadge role={invite.role} />
                  <span className="text-muted-foreground text-xs">
                    Expires {formatDate(invite.expiresAt)}
                  </span>
                  <button
                    onClick={() =>
                      setConfirmTarget({
                        type: 'revoke-invite',
                        id: invite.id,
                        label: invite.email,
                      })
                    }
                    className="text-muted-foreground rounded p-1 transition-colors hover:text-red-600"
                    title="Revoke invitation"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Danger zone — SUPER_ADMIN only                                     */}
      {/* ------------------------------------------------------------------ */}
      {isSuperAdmin && teamId && (
        <div className="rounded-xl border border-red-200 bg-red-50/50 p-6 dark:border-red-900/40 dark:bg-red-900/10">
          <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-red-700 dark:text-red-300">
            <AlertTriangle className="h-5 w-5" />
            Danger zone
          </h2>
          <p className="text-muted-foreground mb-4 text-sm">
            Permanently delete this team and all its MongoDB resources (projects, plugin configs,
            ecommerce orders, CRM contacts, blog posts, generation/build jobs). Cloudflare Worker
            scripts and R2 data are NOT cleaned up automatically — you’ll get a warning toast with
            the manual steps. There is no undo.
          </p>
          <button
            type="button"
            onClick={() => {
              setDeleteTypedName('');
              setDeleteOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4" />
            Delete team
          </button>
        </div>
      )}

      {/* Type-name-to-confirm delete modal */}
      {deleteOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !deleting && setDeleteOpen(false)}
        >
          <div
            className="bg-card w-full max-w-md rounded-xl border shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Delete team "{teamName}"?
              </h2>
              <p className="text-muted-foreground mb-4 text-sm">
                This will delete all data owned by this team. Resources NOT cleaned up: Cloudflare
                Worker scripts, R2 storage, and payment-provider subscriptions — you’ll get the
                cleanup commands in a warning toast.
              </p>
              <label className="mb-3 block text-sm">
                Type <strong>{teamName}</strong> to confirm:
              </label>
              <input
                type="text"
                value={deleteTypedName}
                onChange={(e) => setDeleteTypedName(e.target.value)}
                disabled={deleting}
                placeholder={teamName}
                className="bg-background mb-4 w-full rounded-lg border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteOpen(false)}
                  disabled={deleting}
                  className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTeam}
                  disabled={deleting || deleteTypedName !== teamName}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Delete team forever'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Confirm Dialog                                                     */}
      {/* ------------------------------------------------------------------ */}
      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title={confirmTarget?.type === 'remove-member' ? 'Remove Member' : 'Revoke Invitation'}
        message={
          confirmTarget?.type === 'remove-member'
            ? `Remove ${confirmTarget.label} from the team? They will lose access to all team projects.`
            : `Revoke the invitation to ${confirmTarget?.label ?? ''}?`
        }
        confirmLabel={confirmTarget?.type === 'remove-member' ? 'Remove' : 'Revoke'}
        danger
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
